import {
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeGrid,
  TePlaceholder,
  TeSpacing,
} from '@topcon/tuicl-react';
import styled from 'styled-components';

import { background } from '../../providers/theme/theme.index';
import { Section } from '../../utils/styling';

type OverviewLoaderVariant = 'organization' | 'personal';

type OverviewLoaderProps = {
  variant: OverviewLoaderVariant;
};

const CircleSlot = styled.div<{ $size: number }>`
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;

  te-placeholder {
    border-radius: 50%;
  }
`;

const BarSlot = styled.div<{ $height: number; $width?: number | string; $radius?: number }>`
  height: ${(props) => props.$height}px;
  width: ${(props) =>
    typeof props.$width === 'number' ? `${props.$width}px` : (props.$width ?? '100%')};
  max-width: 100%;
  min-width: 0;
  border-radius: ${(props) => props.$radius ?? 4}px;
  overflow: hidden;

  /* TePlaceholder's shimmer strip sets a large min-width; keep it clipped to the slot. */
  te-placeholder {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
`;

const RowCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 8px;
  background: ${background.secondary};
`;

const KpiSlot = styled.div`
  width: 100%;
  height: 96px;
  border-radius: 8px;
  overflow: hidden;
  background: ${background.secondary};
`;

const CardWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 320px;
  display: flex;
  overflow: hidden;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

function SkeletonBar({
  height,
  width,
  radius,
}: {
  height: number;
  width?: number | string;
  radius?: number;
}) {
  return (
    <BarSlot $height={height} $width={width} $radius={radius}>
      <TePlaceholder height={height} width={typeof width === 'number' ? width : undefined} />
    </BarSlot>
  );
}

function SkeletonCircle({ size }: { size: number }) {
  return (
    <CircleSlot $size={size}>
      <TePlaceholder height={size} width={size} />
    </CircleSlot>
  );
}

function SectionHeaderSkeleton() {
  return (
    <>
      <TeContainer alignItems="center" justifyContent="space-between" fill gap="xs">
        <SkeletonBar height={20} width={120} />
        <SkeletonBar height={16} width={96} />
      </TeContainer>
      <TeDivider dividerColor="border.secondary" />
    </>
  );
}

function KpiSectionSkeleton({ cardCount }: { cardCount: number }) {
  return (
    <Section>
      <SectionHeaderSkeleton />
      <TeGrid gap="s">
        {Array.from({ length: cardCount }, (_, index) => (
          <KpiSlot key={index}>
            <TePlaceholder />
          </KpiSlot>
        ))}
      </TeGrid>
    </Section>
  );
}

function DivisionRowSkeleton() {
  return (
    <RowCard>
      <SkeletonCircle size={64} />
      <TeContainerSlim column gap="xs" grow>
        <SkeletonBar height={18} width={180} />
        <SkeletonBar height={16} width={72} radius={12} />
      </TeContainerSlim>
    </RowCard>
  );
}

function AppRowSkeleton() {
  return (
    <RowCard>
      <SkeletonCircle size={48} />
      <TeContainerSlim column gap="xs2" grow>
        <SkeletonBar height={18} width={160} />
        <SkeletonBar height={14} width="70%" />
      </TeContainerSlim>
      <SkeletonBar height={32} width={88} radius={4} />
    </RowCard>
  );
}

/** Mirrors `components/Card` layout with generic placeholder shapes (no icons). */
function CardSkeleton() {
  return (
    <CardWrapper>
      <TeContainer
        column
        backgroundColor="background.secondary"
        borderRadius={4}
        grow
        justifyContent="space-between"
      >
        <CardBody>
          <TeContainerSlim height={80} width={80} justifyContent="center" alignItems="center">
            <SkeletonCircle size={48} />
          </TeContainerSlim>
          <TeSpacing direction="y" size="80" />
          <SkeletonBar height={18} width="60%" />
          <SkeletonBar height={14} width="90%" />
          <SkeletonBar height={14} width="75%" />
        </CardBody>
        <TeContainerSlim padding={['xs', 0]}>
          <SkeletonBar height={14} width={100} />
        </TeContainerSlim>
      </TeContainer>
    </CardWrapper>
  );
}

function OrganizationOverviewLoader() {
  return (
    <>
      <Section>
        <TeContainer padding="l" fill alignItems="center" gap="m">
          <SkeletonCircle size={64} />
          <TeContainerSlim column gap="s" grow>
            <SkeletonBar height={24} width={220} />
            <TeContainerSlim gap="xs">
              <SkeletonBar height={20} width={64} radius={12} />
              <SkeletonBar height={20} width={88} radius={12} />
            </TeContainerSlim>
          </TeContainerSlim>
        </TeContainer>
      </Section>

      <Section>
        <SectionHeaderSkeleton />
        <TeContainer column gap="s" fill>
          <DivisionRowSkeleton />
          <DivisionRowSkeleton />
        </TeContainer>
      </Section>

      <KpiSectionSkeleton cardCount={4} />
      <KpiSectionSkeleton cardCount={3} />

      <Section>
        <SectionHeaderSkeleton />
        <TeContainer column gap="s" fill>
          <AppRowSkeleton />
          <AppRowSkeleton />
        </TeContainer>
      </Section>
    </>
  );
}

function PersonalOverviewLoader() {
  return (
    <>
      <Section>
        <TeContainer fill column padding="xl" gap="xs" alignItems="center">
          <SkeletonCircle size={96} />
          <SkeletonBar height={24} width={180} />
          <SkeletonBar height={16} width={220} />
        </TeContainer>
      </Section>

      <Section>
        <TeGrid gap="s">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </TeGrid>
      </Section>
    </>
  );
}

/**
 * Layout-stable skeleton for the Overview page while identity / business-partner
 * / dashboard data settle. Uses tuicl `TePlaceholder` (shimmer) rather than
 * icon-shaped ghosts, so ligature FOIC never reaches the user.
 */
function OverviewLoader({ variant }: OverviewLoaderProps) {
  return (
    <TeContainer column grow padding="xs" gap="xs" overflow="auto" alignItems="center">
      {variant === 'organization' ? <OrganizationOverviewLoader /> : <PersonalOverviewLoader />}
    </TeContainer>
  );
}

export default OverviewLoader;
